import { RaycastLine, FacetVisibility, UpdateFacetOnModelMessage, convertToNumber3, SelectionHighlightFacet } from "flavr-common";
import { Vector3, Quaternion } from "flavr-math";
import { RequestRaycastClosestIntersectionMessage, ResponseRaycastClosestIntersectionMessage } from "@messages/selection";
import { now } from "@messages/timing";
import { modelPool } from "@modules/model";
import { COLOR_FOREST_GREEN } from "../../../ui/style/predefined-colors";
const COLOR_MAROON_HEX = parseInt(COLOR_FOREST_GREEN.replace("#", ""), 16);
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const NEXT_RAYCAST_UPDATE_TIME = 1000 / 30;
export class RaySelect {
    constructor(connection, controllerUid, origin, rotation, params) {
        var _a, _b, _c, _d, _e;
        this.connection = connection;
        this.controllerUid = controllerUid;
        this.lastRaycastUpdateTime = 0;
        this.waitForRayResponse = false;
        this.maxRayLength = (_a = params.maxLength) !== null && _a !== void 0 ? _a : 1000;
        this.rayVisible = (_b = params.visible) !== null && _b !== void 0 ? _b : true;
        this.rayColor = (_c = params.color) !== null && _c !== void 0 ? _c : 0x06316d;
        this.rayHeadLength = (_d = params.headLength) !== null && _d !== void 0 ? _d : 0.05;
        this.rayHeadWidth = (_e = params.headWidth) !== null && _e !== void 0 ? _e : 0.01;
        this.origin = new Vector3().fromArray(origin);
        this.rotation = new Quaternion().fromArray(rotation);
        this.direction = new Vector3(0, 0, -1).applyQuaternion(this.rotation);
    }
    hide() {
        if (this.rayVisible) {
            this.connection.send(new UpdateFacetOnModelMessage(this.controllerUid, new FacetVisibility(RaycastLine, false)));
        }
    }
    setRayVisible(visible) {
        if (this.rayVisible === visible)
            return;
        if (visible) {
            this.rayVisible = true;
            this.show();
        }
        else {
            this.hide();
            this.rayVisible = false;
        }
    }
    show() {
        if (this.rayVisible) {
            this.connection.send(new UpdateFacetOnModelMessage(this.controllerUid, new FacetVisibility(RaycastLine, true)));
            this.connection.send(new UpdateFacetOnModelMessage(this.controllerUid, new RaycastLine(convertToNumber3(this.origin), convertToNumber3(tmpVector33.set(0, 0, -1).applyQuaternion(this.rotation)), this.maxRayLength, this.rayColor, false)));
        }
    }
    canSelectionBeUpdated() {
        return !this.waitForRayResponse && now() - this.lastRaycastUpdateTime >= NEXT_RAYCAST_UPDATE_TIME;
    }
    updateSelection(controllerPos, controllerOri, scale, selectionCallback) {
        this.lastRaycastUpdateTime = now();
        this.waitForRayResponse = true;
        const rayOrigin = tmpVector31.copy(this.origin).applyQuaternion(controllerOri).multiplyScalar(scale).add(controllerPos);
        const rayDirection = tmpVector32
            .copy(this.direction)
            .applyQuaternion(controllerOri);
        this.connection.ask(new RequestRaycastClosestIntersectionMessage(convertToNumber3(rayOrigin), convertToNumber3(rayDirection)), (answer) => {
            var _a;
            this.waitForRayResponse = false;
            const msg = answer.length === 1 ? answer[0] : undefined;
            if ((msg === undefined) || !ResponseRaycastClosestIntersectionMessage.is(msg))
                return;
            if (msg.response !== undefined) {
                selectionCallback({
                    uid: msg.response.uid,
                    hitPoint: msg.response.worldPosition,
                    localPoint: msg.response.localPosition
                });
            }
            else {
                selectionCallback(undefined);
            }
            if (this.rayVisible) {
                const hitNormal = (msg.response !== undefined) ? msg.response.worldNormal : undefined;
                const isSelectableHit = msg.response !== undefined &&
                    ((_a = modelPool.getModel(msg.response.uid)) === null || _a === void 0 ? void 0 : _a.getFacet(SelectionHighlightFacet)) !== undefined;
                const lineColor = isSelectableHit ? COLOR_MAROON_HEX : this.rayColor;
                this.connection.send(new UpdateFacetOnModelMessage(this.controllerUid, new RaycastLine(convertToNumber3(this.origin), convertToNumber3(this.direction), (msg.response !== undefined) ? msg.response.distance / scale : this.maxRayLength, lineColor, false, hitNormal)));
            }
        });
    }
}
